import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ArrowRightIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import ScoreBadge from "../../score-badge";
import { Features } from "../../../entities/features";
import { Permissions } from "../../../entities/permissions";
import { INSIGHT_SCORES, type Insight } from "../../../entities/insights";
import { useIsFeatureEnabled } from "../../../services/api/featuresApi";
import {
  useInsightListQuery,
  useInsightSummaryQuery,
} from "../../../services/api/insightsApi";
import { hasPermission } from "../../../pages/login/selectors";

const PREVIEW_COUNT = 3;

/*
 * Insights summary for the main dashboard.
 *
 * Renders nothing at all when the feature is off or the user lacks the
 * permission — returning null rather than an empty card keeps the dashboard
 * grid from developing a hole for users who cannot use the feature.
 */
export default function InsightsSummaryCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const canView = useSelector(hasPermission(Permissions.PAGE_INSIGHTS_ROOT));
  const featureEnabled = useIsFeatureEnabled(Features.FT_INSIGHTS);
  const enabled = canView && featureEnabled;

  const { data: summary, isLoading } = useInsightSummaryQuery(undefined, { skip: !enabled });
  const { data: list } = useInsightListQuery(
    { range: { page: 1, limit: PREVIEW_COUNT }, filters: { status: "open" } },
    { skip: !enabled },
  );

  if (!enabled) return null;

  const items = list?.items ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>{t("insights.page-title", "Insights")}</CardTitle>
        <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard/insights")}>
          {t("insights.see-all", "See all")} <ArrowRightIcon />
        </Button>
      </CardHeader>

      <CardContent className="space-y-4">
        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          <>
            <div className="flex flex-wrap gap-4">
              {INSIGHT_SCORES.map((score) => (
                <div key={score} className="flex items-center gap-2">
                  <ScoreBadge score={score} compact />
                  <span className="text-2xl font-semibold">
                    {summary?.byScore?.[score] ?? 0}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {t(`insights.score.${score}`, score)}
                  </span>
                </div>
              ))}
            </div>

            {items.length ? (
              <ul className="space-y-2">
                {items.map((insight: Insight) => (
                  <li key={insight.fingerprint}>
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 rounded-md p-1 text-left hover:bg-accent/50"
                      onClick={() =>
                        navigate(`/dashboard/insights?detail=${insight.fingerprint}`)
                      }
                    >
                      <ScoreBadge score={insight.score} compact />
                      <span className="truncate text-sm">{insight.title}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                {summary?.reason === "insufficient-data"
                  ? t(
                      "insights.empty.insufficient-data",
                      "Not enough inspection media yet to find patterns.",
                    )
                  : t("insights.empty.dashboard", "Nothing to review right now.")}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
